import { InjectionToken } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { UserService } from '../api';
import { TiveSessionService } from './tive-session.service';

/** Shared promise so concurrent callers trigger only one refresh cycle. */
let refreshInProgress: Promise<void> | null = null;

/**
 * Force-refresh Auth0 access token and re-establish the Tive backend session.
 *
 * Concurrent calls are coalesced into a single round-trip.
 */
export function refreshSession(
  auth: AuthService,
  userService: UserService,
  session: TiveSessionService,
): Promise<void> {
  if (!refreshInProgress) {
    refreshInProgress = doRefresh(auth, userService, session).finally(() => {
      refreshInProgress = null;
    });
  }
  return refreshInProgress;
}

/** DI token so components can inject (and tests can mock) the refresh function. */
export const REFRESH_SESSION = new InjectionToken<typeof refreshSession>('refreshSession', {
  providedIn: 'root',
  factory: () => refreshSession,
});

async function doRefresh(
  auth: AuthService,
  userService: UserService,
  session: TiveSessionService,
): Promise<void> {
  console.warn('[session-refresh] Refreshing Auth0 token + Tive session …');

  // 1. Bypass the SDK cache so the auth interceptor attaches a fresh token
  await firstValueFrom(auth.getAccessTokenSilently({ cacheMode: 'off' }));

  // 2. Re-call loginByEmail so the backend sees a valid session again
  const profile = await firstValueFrom(auth.user$);
  if (profile?.email) {
    const loginUser = await firstValueFrom(
      userService.apiUserLoginByEmailPost(undefined, JSON.stringify(profile.email)),
    );
    session.setLoginResponse(loginUser);
    console.warn('[session-refresh] Session restored for', profile.email);
  } else {
    console.error('[session-refresh] No user email available — cannot re-establish session');
  }
}
